/**
 * TKP-237 t.8 (karar 2026-11-01) — toplu işlemin bütün istekleri aynı `X-Draft-Batch` başlığını taşır; onay açıkken sunucu taslak satırlarına
 * işlem bilgisini yazar, onay ekranları bunları tek satırda gösterir. Onay kapalıyken başlık yok sayılır. Bilgi amaçlıdır (yetki değil).
 */
let current = null;

/** Kesicinin okuduğu başlık değeri (yoksa null). */
export const draftBatchHeader = () => current;

export const newBatchId = () => `b_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/** UTF-8 güvenli base64 (Türkçe adlar). */
export const encodeBatch = (meta) => btoa(unescape(encodeURIComponent(JSON.stringify(meta))));

/** `fn` süresince yapılan istekler aynı işlem kimliğini taşır; iç içe çağrıda dıştaki geri yüklenir. */
export const withDraftBatch = async (meta, fn) => {
  const previous = current;
  current = meta ? encodeBatch({ id: newBatchId(), ...meta }) : previous;
  try {
    return await fn();
  } finally {
    current = previous;
    if (!previous && batchDrafted) {
      // TKP-241 p.4: işlem içinde taslağa düşen istekler taslak listesini tek tek yenilemez — işlem bitince bir kez
      batchDrafted = false;
      if (onBatchDrafted) await Promise.resolve(onBatchDrafted()).catch(() => undefined);
    }
  }
};

let batchDrafted = false;
let onBatchDrafted = null;
/** Kesici: işlem içindeki bir istek taslağa düştü. */
export const markBatchDrafted = () => {
  if (current) batchDrafted = true;
};
/** İşlem taslağa yazdıysa bitişte çağrılacak yenileme (kesici kaydeder: taslak listesini getirir). */
export const setBatchDraftedHandler = (fn) => {
  onBatchDrafted = fn;
};

/**
 * TKP-242 q.1 — toplu taslak işlemi ARKA PLANDA: `fn`in yaptığı yazma istekleri gönderilmez, kaydedilir (yöntem + yol + gövde);
 * sonra tek istekle sunucuya verilir (`post({ batch, steps })`), sunucu aynı uçları sırayla oynatır ve bitince tek bildirim gönderir.
 * Okuma istekleri (GET) olduğu gibi gider. Adım planları ve store eylemleri değişmez — yalnız iletim yolu değişir.
 */
let recording = null;
const WRITE_METHODS = ["post", "put", "delete", "patch"];
const parseData = (data) => {
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch (e) {
    return data;
  }
};
/** Kesici: bu istek kaydedilecek mi (kayıt kipinde yazma isteği). */
export const shouldRecord = (config) => !!recording && WRITE_METHODS.includes(String((config && config.method) || "get").toLowerCase());
/** Axios bağdaştırıcısı: isteği kaydeder, gönderilmiş gibi boş yanıt döner. */
/** TKP-254: kayıt tamamen mikro görevlerde koşuyordu — ekran (onay penceresi dahil) kayıt bitene kadar çizilemiyordu; her N adımda bir sıra verilir. */
export const RECORD_YIELD_EVERY = 50;
export const recordingAdapter = (config) => {
  if (recording) recording.push({ method: String(config.method || "post").toLowerCase(), url: String(config.url || ""), data: parseData(config.data) });
  const response = { data: { __recorded: true }, status: 200, statusText: "OK", headers: {}, config, request: { responseURL: "" } };
  if (recording && recording.length % RECORD_YIELD_EVERY === 0) return new Promise((resolve) => setTimeout(() => resolve(response), 0));
  return Promise.resolve(response);
};
export const runInBackground = async (meta, fn, post) => {
  const steps = [];
  const previousRecording = recording;
  const previous = current;
  recording = steps;
  current = meta ? encodeBatch({ id: newBatchId(), ...meta }) : previous;
  const batch = current;
  try {
    await fn();
  } finally {
    recording = previousRecording;
    current = previous;
  }
  if (!steps.length) return { total: 0 };
  const response = await post({ batch, steps });
  return { total: steps.length, ...((response && response.data) || {}) };
};

/** TKP-242: işlem bilgisindeki hedef listesi en çok `max` karakter (sunucu 2000'de keser; başlık sınırı 8 KB) — fazlası "+N". */
export const clipTargets = (names = [], max = 600) => {
  const out = [];
  let length = 0;
  for (const name of names.filter(Boolean)) {
    if (length + name.length + 2 > max) return `${out.join(", ")} +${names.filter(Boolean).length - out.length}`;
    out.push(name);
    length += name.length + 2;
  }
  return out.join(", ");
};
